import { Trash2, Check, Inbox, Download } from 'lucide-react';
import { useServiceSubmissions, useSetSubmissionStatus, useDeleteSubmission } from './hooks.ts';
import { downloadFile } from '../../lib/download.ts';
import type { FormField, ServiceSubmission } from './api.ts';

function fmtDate(iso: string): string {
  // Stable, locale-free formatting (no Date.now dependence).
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function labelFor(fields: FormField[], id: string): string {
  const f = fields.find((x) => x.id === id);
  return f ? (f.labelEn || f.labelDv || id) : id;
}

function Row({ sub, fields, serviceId }: { sub: ServiceSubmission; fields: FormField[]; serviceId: string }): JSX.Element {
  const setStatus = useSetSubmissionStatus(serviceId);
  const del = useDeleteSubmission(serviceId);
  const files = sub.files ?? {};
  const fileIds = new Set(Object.keys(files));
  // Text answers (exclude fields that carry an uploaded file — shown as downloads).
  const entries = Object.entries(sub.data).filter(([k]) => !fileIds.has(k));
  return (
    <li className={`rounded-lg border p-3 ${sub.status === 'new' ? 'border-primary/40 bg-primary/5' : 'border-border bg-paper'}`}>
      <div className="mb-2 flex items-center gap-2">
        {sub.status === 'new' && <span className="pill pill-active">New</span>}
        <span className="mono-meta text-xs text-muted-foreground">{fmtDate(sub.createdAt)}</span>
        <div className="ms-auto flex items-center gap-1.5">
          {sub.status === 'new' && (
            <button type="button" onClick={() => setStatus.mutate({ id: sub.id, status: 'read' })} disabled={setStatus.isPending} className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Check size={13} /> Mark read</button>
          )}
          <button type="button" aria-label="Delete submission" onClick={() => { if (confirm('Delete this submission?')) del.mutate(sub.id); }} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
        </div>
      </div>
      <dl className="grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
        {entries.length === 0 && Object.keys(files).length === 0 && <span className="text-muted-foreground">(no answers)</span>}
        {entries.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="font-medium text-muted-foreground">{labelFor(fields, k)}</dt>
            <dd className="text-foreground break-words">{v || <span className="text-muted-foreground">—</span>}</dd>
          </div>
        ))}
        {Object.entries(files).map(([k, f]) => (
          <div key={k} className="contents">
            <dt className="font-medium text-muted-foreground">{labelFor(fields, k)}</dt>
            <dd>
              <button
                type="button"
                onClick={() => void downloadFile(`/site/submissions/${sub.id}/file/${k}`, f.name)}
                className="inline-flex items-center gap-1.5 text-primary hover:underline"
              >
                <Download size={13} /> {f.name}
              </button>
            </dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

// The submissions inbox for one service. Loads on mount (rendered only when the
// staff open the disclosure).
export function SubmissionsPanel({ serviceId, fields }: { serviceId: string; fields: FormField[] }): JSX.Element {
  const { data: submissions, isLoading, isError } = useServiceSubmissions(serviceId, true);

  if (isLoading) return <p className="p-2 text-sm text-muted-foreground">Loading submissions…</p>;
  if (isError) return <p className="p-2 text-sm text-destructive">Couldn't load submissions.</p>;
  if (!submissions || submissions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 p-6 text-center">
        <Inbox size={20} className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No applications yet.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {submissions.map((s) => <Row key={s.id} sub={s} fields={fields} serviceId={serviceId} />)}
    </ul>
  );
}
